import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileSignature, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageLoading, fmtDate } from "@/components/shared";
import { useReadOnly } from "./common";
import { StatusBadge } from "./scBadges";
import api from "@/lib/api";

export async function startContract(nav, subcontractor_id, site_id) {
  const { data } = await api.post("/subcon/contracts", { subcontractor_id, site_id: site_id || null });
  nav(`/app/employer/subcontractors/contracts/${data.id}`);
}

export default function SubContracts() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const readOnly = useReadOnly();
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(params.get("new") === "1");
  useEffect(() => { api.get("/subcon/contracts").then((r) => setRows(r.data.contracts)).catch(() => setRows([])); }, []);
  return (
    <div data-testid="sub-contracts">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-xs text-slate-500">{t("sc.disclaimer")}</p>
        {!readOnly && <Button onClick={() => setOpen(true)} data-testid="sc-create-btn" className="rounded-full bg-navy text-white hover:bg-navy-muted"><Plus className="mr-1 h-4 w-4" /> {t("sc.create")}</Button>}
      </div>
      {rows === null ? <PageLoading /> : rows.length === 0 ? <EmptyState icon={FileSignature} title={t("sc.empty")} /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full min-w-[640px] text-sm" data-testid="sc-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("sc.colSub")}</th><th className="px-4 py-3">{t("sc.colProject")}</th><th className="px-4 py-3">{t("sc.colVersion")}</th><th className="px-4 py-3">{t("sc.colStatus")}</th><th className="px-4 py-3">{t("sc.period.start_date")}</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} onClick={() => nav(`/app/employer/subcontractors/contracts/${c.id}`)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" data-testid={`sc-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{c.subcontractor_name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.project_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">V{c.version}</td>
                  <td className="px-4 py-3"><StatusBadge v={c.status} /></td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate((c.snapshot || c.data || {}).start_date) || "—"}</td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-slate-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateDialog open={open} onClose={() => setOpen(false)} preset={params.get("sub")} />
    </div>
  );
}

function CreateDialog({ open, onClose, preset }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [subs, setSubs] = useState([]);
  const [sites, setSites] = useState([]);
  const [sub, setSub] = useState(preset || "");
  const [site, setSite] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/subcon/companies").then((r) => setSubs(r.data.companies || [])).catch(() => {});
    api.get("/subcon/sites").then((r) => setSites(r.data.sites || [])).catch(() => {});
  }, [open]);
  const go = async () => {
    setBusy(true);
    try { await startContract(nav, sub, site === "none" ? "" : site); } catch { toast.error("Error"); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="sc-create-dialog">
        <DialogHeader><DialogTitle>{t("sc.create")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={sub} onValueChange={setSub}><SelectTrigger data-testid="sc-create-sub"><SelectValue placeholder={t("sc.chooseSub")} /></SelectTrigger>
            <SelectContent>{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.trading_name || s.legal_name}</SelectItem>)}</SelectContent></Select>
          <Select value={site} onValueChange={setSite}><SelectTrigger data-testid="sc-create-site"><SelectValue placeholder={t("sc.chooseSite")} /></SelectTrigger>
            <SelectContent><SelectItem value="none">—</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <DialogFooter><Button disabled={!sub || busy} onClick={go} data-testid="sc-create-confirm" className="rounded-full bg-cyan text-white hover:bg-cyan-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sc.next")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
